import { Network } from "../../fstn/network";
import { connect } from "react-redux";
import { Box, Card, List } from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Cursor } from "../../fstn/cursor";
import { ActionFactory } from "../../fstn/actionFactory";
import { selectComments, selectDictionary, selectGrammar, selectIgnoredGrammar, selectLocks, selectProjects, selectUser }
    from "../../redux/selectors/firebase";
import { PathwayUser, Projects, ProjectRoles } from "../../redux/types/firebase";
import State from "./State";
import { VariableSizeList } from "react-window";

import { Comments, CommentType, IScriptComment } from "../comments/CommentContainer";
import StateCommentsHeatmap from "../comments/StateCommentsHeatmap";
import CommentsBox from "./Comments";
import { updateFocus, updateModeData } from "../../redux/slices/editor";
import { selectModeData, selectSearchText, selectUserId, selectWriterMode } from "../../redux/selectors/editor";
import { selectFocus, selectMode } from "../../redux/selectors/editor";
import { Transition } from "../../fstn/transition";
import { IValues } from "./ScriptRender";
import { FirebaseContext } from "../firebase/FirebaseProvider";
import { Locks } from "../navbar/StateLock";
import Dictionary from "./Dictionary";
import ScriptListItem from "./ScriptListItem";
import { IGrammarRule } from "./EditState";

const useScriptStyles = makeStyles(theme => ({
    card: {
        display: 'flex',
        flexDirection: "column",
        flexGrow: 1,
        padding: theme.spacing(1)
    },
    content: {
        flexGrow: 1,
        overflowY: 'auto',
        fontSize: "large"
    },
    list: {
        '&::-webkit-scrollbar': {
            width: '0.4em'
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,.1)',
            borderRadius: '0.4em'
        },
        overflowX: "hidden"
    },
    screen: {
        margin: theme.spacing(2),
        marginRight: theme.spacing(16),
        position: 'absolute',
        display: 'flex',
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
    },
    state: {
        marginTop: theme.spacing(2)
    }
}));

export type Choices = { [key: string]: string };
type ReversePath = { [key: string]: string[] };
export interface IGrammarMatch
{
    context: { length: number, offset: number, text: string }
    length: number
    offset: number
    message: string
    shortMessage: string
    rule: {
        id: string
        description: string
        issueType: string
        urls: IValues[]
    }
    replacements?: IValues[]
}

export interface IIgnoredGrammar
{
    length: number
    offset: number
    rule: string
    text: string,
    context: string
}
export type IgnoredGrammar = { [state: string]: { [id: string]: IIgnoredGrammar } };
export interface IGrammar
{
    text: string
    matches: IGrammarMatch[]
}
export type Grammar = { [state: string]: IGrammar };
export interface IScript
{
    text: string
    grammar: IGrammarMatch[]
    ignoredGrammar: IIgnoredGrammar[]
}
export interface IStateComment
{
    comment: string
    end: number
    start: number
}
export type StateCommentMap = { [state: string]: IStateComment[] }
interface IProps
{
    comments: Comments
    dictionary: { [word: string]: any }
    locks: Locks
    mode: string
    focus: string
    factory: ActionFactory
    grammar: Grammar
    ignoredGrammar: IgnoredGrammar
    modeData: { [key: string]: any }
    network: Network
    project: string
    projects: Projects
    updateFocus: (focus: string) => void
    updateModeData: (update: { [key: string]: any }) => void
    userKey: string
    writerMode: "network" | "grammar",
    user: PathwayUser
}
function Writer({ comments, dictionary, factory, focus, grammar, ignoredGrammar, locks, mode, modeData, network,
    project, projects, updateModeData, userKey, writerMode, user }: IProps)
{
    const classes = useScriptStyles();
    const [choices, setChoices] = useState<Choices>({});
    const [commentHighlight, setCommentHighlight] = useState<string>();
    const [dictionaryWords, setDictionaryWords] = useState<string[]>([]);
    const [editing, setEditing] = useState<string | undefined>();
    const [firstRender, setFirstRender] = useState(true);
    const [heights, setHeights] = useState<number[]>([]);
    const [stateHighlight, setStateHighlight] = useState<string>();
    const [listHeight, setListHeight] = useState<number>(0);
    const [stateCommentMap, setStateCommentMap] = useState<StateCommentMap>({});
    const [states, setStates] = useState<string[]>([]);
    const commentsRefs = useRef<Array<HTMLDivElement>>([]);
    const [variableSizeListRef, setVariableSizeListRef] = useState<VariableSizeList | null>(null);
    const [reversePath, setReversePath] = useState<ReversePath>({});
    const [lastScrollTo, setLastScrollTo] = useState("");
    const variableSizeListEl = useCallback((node) =>
    {
        setVariableSizeListRef(node);
    }, [setVariableSizeListRef]);
    const listRef = useRef<HTMLDivElement>(null);
    const selectionRangeRef = useRef({ start: 0, end: 0 });
    const { removeLock, setLock, setScript } = useContext(FirebaseContext);
    const isLocked = useCallback((stateKey: string, network: Network, locks: Locks, userKey: string): boolean =>
    {
        if (locks && stateKey)
        {
            if (locks[stateKey]) {
                return locks[stateKey].user !== userKey;
            } else {
                const state = network.states.get(stateKey);

                if (state && state.parent && locks[state.parent]) {
                    return locks[state.parent].user !== userKey;
                }
            }
        }
        return false;
    }, []);
    const { setGrammar } = useContext(FirebaseContext);

    const admin = user.roles.admin;
    const canEdit = admin || user.roles.editor || (user.roles as ProjectRoles)[project]?.editor;
    const canAddComment = (canEdit || user.roles.reviewer || (user.roles as ProjectRoles)[project]?.reviewer) === true;

    const handleEdit = useCallback((stateKey?: string) =>
    {
        if (canEdit)
        {
            if (stateKey && !isLocked(stateKey, network, locks, userKey))
            {
                const state = network.states.get(stateKey);
                
                if (state) {
                    setLock(project, state.parent, userKey).then(() =>
                    {
                        setEditing(stateKey);
                        setStateHighlight(stateKey);
                    });
                }
            } else
            {
                if (editing) {
                    const state = network.states.get(editing);

                    if (state && locks[state.parent]?.user === userKey) {
                        removeLock(project, state.parent).then(() => setEditing(undefined))
                    } else {
                        console.error(`state ${editing} was locked but not by us, we should never have been editing`);
                        setEditing(undefined);
                    }
                }
            }
        }
    }, [canEdit, editing, isLocked, locks, network, project, removeLock, setLock, userKey]);

    const handleChoiceChange = (state: string, choice: string) =>
    {
        setChoices(choices => ({ ...choices, [state]: choice }));
    };
    const handleUpdateText = useCallback((index: number, stateKey: string, update: string) =>
    {
        const state = network.states.get(stateKey);

        if (state)
        {
            return setScript(project, stateKey, state.script, update, userKey).then(() =>
            {
                removeLock(project, state.parent).then(() =>
                {
                    setEditing(undefined);
                });
            });
        }
        return Promise.resolve();
    }, [network.states, project, removeLock, setScript, userKey]);
    const handleUpdateGrammar = useCallback((state: string, text: string, update: IGrammarRule[]) =>
    {
        const matches = update.filter(match =>
            dictionaryWords.indexOf(text.substr(match.offset, match.length).trim().toLowerCase()) === -1);

        if (matches.length)
        {
            setGrammar(project, state, {
                matches,
                text
            }).catch(console.error);
        }
    }, [dictionaryWords, project, setGrammar]);
    const handleSetHeight = useCallback((index: number, height: number) =>
    {
        setHeights((heights) =>
        {
            heights[index] = height;
            return heights;
        });
        variableSizeListRef?.resetAfterIndex(index);
    }, [variableSizeListRef])
    const handleClickNote = (index: number) =>
    {
        if (!editing && variableSizeListRef)
        {
            variableSizeListRef.scrollToItem(index);
            setStateHighlight(states[index]);
        }
    }
    const handleHighlightState = (stateId?: string) => setStateHighlight(stateId);
    const handleHighlightComment = (commentId?: string) => setCommentHighlight(commentId);
    const handleToggleShowDictionary = () => { updateModeData({ showDictionary: !modeData.showDictionary }) }

    const getItemSize = (index: number): number => heights[index] || 0;

    const getCommentTypeInfo = useCallback((): CommentType =>
    {
        return {
            name: "script",
            state: stateHighlight as string,
            range: { start: selectionRangeRef.current.start, end: selectionRangeRef.current.end }
        }
    }, [stateHighlight]);

    const updateSelectionRange = (state: string, start: number, end: number) =>
    {
        selectionRangeRef.current = { start, end };
    }
    const { trackComments, trackDictionary, trackGrammar, trackIgnoredGrammar } = useContext(FirebaseContext);

    useEffect(() =>
    {
        setDictionaryWords(Object.keys(dictionary || {}).map(word => word.toLowerCase()));
    }, [dictionary, setDictionaryWords]);
    useEffect(() =>
    {
        const stateCommentMap: StateCommentMap = {};

        Object.keys(comments || {}).forEach(commentKey =>
        {
            const comment = comments[commentKey];

            if (comment.type.name === "script" && !(comment.resolved || comment.deleted))
            {
                const scriptComment = comment.type as IScriptComment;

                if (stateCommentMap[scriptComment.state] === undefined)
                {
                    stateCommentMap[scriptComment.state] = [];
                }

                stateCommentMap[scriptComment.state].push({
                    comment: commentKey,
                    end: scriptComment.range.end,
                    start: scriptComment.range.start
                });
            }
        });
        setStateCommentMap(stateCommentMap);
    }, [comments, setStateCommentMap]);
    useEffect(() =>
    {
        const reversePath: ReversePath = {};
        const addEntry = (from: string, to: string) =>
        {
            if (reversePath[to] === undefined)
            {
                reversePath[to] = [];
            }
            if (!reversePath[to].includes(from))
            {
                reversePath[to].push(from);
            }
        };

        network.states.forEach((state, key) =>
        {
            if (state.child)
            {
                addEntry(key, state.child);
            }
            state.transitions.forEach(transition =>
            {
                if (transition.destination)
                {
                    addEntry(key, transition.destination);
                }
            });
        });
        setReversePath(reversePath);
    }, [network, setReversePath]);
    useEffect(() =>
    {
        const buildNetworkStates = (network: Network, choices: { [key: string]: string }): string[] =>
        {
            const visited: string[] = [];
            const states: string[] = [];

            if (!network) return states;

            const cursor = new Cursor(network, factory);

            while (!visited.includes(cursor.state))
            {
                const stateKey = cursor.state;
                const triggers = cursor.triggers;
                const script = network.states.get(stateKey)?.script;
                let trigger = 'continue';

                visited.push(stateKey);
                if (script)
                {
                    states.push(stateKey);
                }
                if (triggers.length > 1 || triggers[0] !== "continue")
                {
                    trigger = choices[stateKey] || triggers[0];
                    choices[stateKey] = trigger;
                }
                cursor.trigger(trigger);
            }
            return states;
        };

        if (writerMode === "network")
        {
            setStates(buildNetworkStates(network, choices));
        }
    }, [choices, factory, network, setStates, writerMode]);
    useEffect(() =>
    {
        const buildGrammarStates = (grammar: Grammar): string[] =>
        {
            return Object.keys(grammar || {}).filter(state =>
                grammar[state].matches && grammar[state].matches.length > 0);
        };

        if (writerMode === "grammar")
        {
            setStates(buildGrammarStates(grammar));
        }
    }, [grammar, setStates, writerMode]);
    useEffect(() =>
    {
        if (focus === lastScrollTo)
            return;

        if (variableSizeListRef && focus && states.includes(focus))
        {
            const index = states.indexOf(focus);

            if (index >= 0)
            {
                variableSizeListRef.scrollToItem(index, "start");
                setLastScrollTo(focus);
            }
        }
    }, [focus, states, variableSizeListRef, lastScrollTo]);
    useEffect(() =>
    {
        const getKeyStates = (network: Network, start: string, reversePath: ReversePath, visited: string[] = [],
            keyStates: string[] = []): string[] =>
        {
            while (reversePath[start]?.length > 0 && !visited.includes(start) && !keyStates.includes(start))
            {
                visited.push(start);
                if (reversePath[start].length === 1)
                {
                    const precursor = network.states.get(reversePath[start][0]);

                    if (precursor && precursor.transitions.length > 1)
                    {
                        keyStates.push(start);
                    }
                    start = reversePath[start][0];
                } else
                {
                    reversePath[start].forEach(precursor =>
                    {
                        getKeyStates(network, precursor, reversePath, visited, keyStates);
                    });
                    break;
                }
            }
            return keyStates;
        }
        const getChoicesToState = (network: Network, target: string, keyStates: string[],
            start: string = network.initial, visited: string[] = []): Choices | undefined =>
        {
            const getTransitionChoices = (transition: Transition): Choices | undefined =>
            {
                const transitionChoices = getChoicesToState(network, target, keyStates, transition.destination,
                    [...visited]);

                if (transitionChoices)
                {
                    return {
                        ...transitionChoices,
                        [start]: transition.trigger as string
                    };
                }
                return undefined;
            };
            let state = network.states.get(start);

            while (state && !visited.includes(start))
            {
                visited.push(start);
                if (start === target)
                {
                    return {};
                }
                if (state.child)
                {
                    // Push network
                    start = state.child;
                    state = network.states.get(start);
                } else if (state.parent === state.transitions[0]?.destination)
                {
                    // Pop network
                    state = network.states.get(state.parent);
                    if (state && state.transitions[0].destination)
                    {
                        start = state.transitions[0].destination;
                        state = network.states.get(start);
                    } else
                    {
                        return undefined;
                    }
                } else if (state.transitions.length > 1)
                {
                    let choices: Choices | undefined;

                    if (state.transitions
                        .filter(transition => transition.destination && !visited.includes(transition.destination))
                        .sort((lhs, rhs) =>
                        {
                            const indexLhs = keyStates.indexOf(lhs.destination!);
                            const indexRhs = keyStates.indexOf(rhs.destination!);

                            return (indexLhs === -1 ? keyStates.length : indexLhs) -
                                (indexRhs === -1 ? keyStates.length : indexRhs);
                        })
                        .some(transition =>
                        {
                            return choices = getTransitionChoices(transition);
                        }))
                    {
                        return choices;
                    }
                } else if (state.transitions.length === 1 && state.transitions[0].destination)
                {
                    start = state.transitions[0].destination;
                    state = network.states.get(start);
                } else
                {
                    return undefined;
                }
            }
            return undefined;
        };

        const state = focus;

        if (state && states.length > 0 && !states.includes(state))
        {
            const keyStates = getKeyStates(network, state, reversePath);

            if (state && states.indexOf(state) === -1)
            {
                const focusChoices = getChoicesToState(network, state, keyStates);

                if (Object.keys(focusChoices || {}).length)
                {
                    setChoices(choices => ({ ...choices, ...focusChoices }));
                } else
                {
                    console.error(`Could not find the set of choices to get to state ${state}`);
                }
            }
        }
    }, [focus, network, reversePath, setChoices, states])
    useEffect(() =>
    {
        if (listRef.current && states.length > 0)
        {
            setListHeight(listRef.current.clientHeight);
        }
    }, [listRef, setListHeight, states]);

    useEffect(() =>
    {
        if (firstRender)
        {
            setFirstRender(listRef.current === null || states.length === 0 || (heights.length !== states.length));
        }
    }, [firstRender, heights.length, states.length]);
    useEffect(() =>
    {
        trackComments(project);
        trackDictionary(project);
        trackGrammar(project);
        trackIgnoredGrammar(project);
    }, [project, trackComments, trackDictionary, trackGrammar, trackIgnoredGrammar]);
    useEffect(() =>
    {
        if (editing && isLocked(editing, network, locks, userKey))
        {
            setEditing(undefined);
        }
    }, [editing, isLocked, locks, network, userKey]);

    const itemData = {
        states,
        network,
        grammar,
        mode,
        ignoredGrammar,
        editing,
        project,
        projects,
        stateCommentMap,
        comments,
        commentHighlight,
        choices,
        stateHighlight,
        canAddComment,
        handleEdit,
        handleUpdateText,
        handleChoiceChange,
        handleUpdateGrammar,
        handleHighlightState,
        handleHighlightComment,
        handleSetHeight,
        updateSelectionRange,
        getCommentTypeInfo,
        commentsRefs
    }

    return (
        <Box className={classes.screen}>
            <CommentsBox comments={commentsRefs.current} states={states} />
            <Card className={classes.card}>
                <div ref={listRef} className={classes.content}>
                    <Box sx={{ display: "flex", flexDirection: "row" }}>
                        {!firstRender ?
                            <VariableSizeList overscanCount={2}
                                className={classes.list} height={listHeight} width="100%" style={{ overflowX: "hidden" }} itemCount={states.length}
                                itemSize={getItemSize} ref={variableSizeListEl} itemData={itemData}>
                                {ScriptListItem}
                            </VariableSizeList> :
                            <List>
                                {states.map((state, index) =>
                                {
                                    const script: IScript = {
                                        text: network.states.get(state)?.script || "",
                                        grammar: (grammar && grammar[state]?.matches) || [],
                                        ignoredGrammar: (ignoredGrammar && state in ignoredGrammar) ? Object.values(ignoredGrammar[state]) : []
                                    }

                                    return (
                                        <State
                                            comments={stateCommentMap[state] || []} highlight={stateHighlight === state}
                                            commentHighlight={commentHighlight} choices={choices} index={index}
                                            key={states[index]} onChoice={handleChoiceChange} onEdit={handleEdit}
                                            onSetHeight={handleSetHeight} updateScript={handleUpdateText}
                                            projectDetails={projects[project]} script={script} state={state}
                                            onStateHighlight={handleHighlightState} onUpdateGrammar={handleUpdateGrammar}
                                            setSelectionRange={updateSelectionRange} />

                                    );
                                })}
                            </List>}
                        <StateCommentsHeatmap onClick={handleClickNote} height={listHeight} getItemSize={getItemSize} states={states} />
                    </Box>
                </div>
                {modeData.showDictionary && <Dictionary onClose={handleToggleShowDictionary} project={project} />}
            </Card>
        </Box >
    )
}
const mapStateToProps = (store: never) => ({
    comments: selectComments(store),
    dictionary: selectDictionary(store),
    focus: selectFocus(store),
    grammar: selectGrammar(store),
    ignoredGrammar: selectIgnoredGrammar(store),
    locks: selectLocks(store),
    mode: selectMode(store),
    modeData: selectModeData(store),
    projects: selectProjects(store),
    searchText: selectSearchText(store),
    userKey: selectUserId(store),
    writerMode: selectWriterMode(store),
    user: selectUser(store)
});

export default connect(mapStateToProps, {
    updateFocus,
    updateModeData
})(Writer);
